import { reactive, readonly } from 'vue';

/**
 * Translation Composable
 * 
 * Provides Vue i18n-style translation API backed by WordPress wp_localize_script().
 * Translations are loaded from window.formychat_admin_vars.strings (Admin) or window.formychat_vars.strings (Frontend).
 * 
 * @since 1.0.0
 */

// Initialize translations from WordPress localized data, checking both Admin and Frontend vars
const translations = reactive(
    window.formychat_admin_vars?.strings ||
    window.formychat_vars?.strings ||
    {}
);

/**
 * Translation composable - Vue i18n style API with WordPress backend
 * 
 * @returns {Object} Translation functions
 * 
 * @example
 * import { useTranslation } from '@composables/useTranslation';
 * 
 * const { t } = useTranslation();
 * 
 * // Basic usage
 * t('save'); // Returns: "Save"
 * 
 * // With fallback
 * t('unknown_key', {}, 'Default Text'); // Returns: "Default Text"
 * 
 * // With interpolation
 * t('hello_name', { name: 'John' }); // Returns: "Hello John"
 */
export function useTranslation() {
    /**
     * Get translated string
     * 
     * @param {string} key - Translation key
     * @param {object} params - Optional interpolation parameters
     * @param {string} fallback - Optional fallback text
     * @returns {string} Translated text
     */
    const t = (key, params = {}, fallback = key) => {
        let text = translations[key] || fallback;

        // Handle interpolation like {name}, {count}
        if (Object.keys(params).length > 0) {
            Object.entries(params).forEach(([param, value]) => {
                text = text.replace(new RegExp(`{${param}}`, 'g'), value);
            });
        }

        return text;
    };

    /**
     * Check if translation exists
     * 
     * @param {string} key - Translation key to check
     * @returns {boolean}
     */
    const hasTranslation = (key) => key in translations;

    /**
     * Get all translations (readonly)
     * 
     * @returns {Readonly<Object>}
     */
    const getAllTranslations = () => readonly(translations);

    return {
        t,
        hasTranslation,
        translations: getAllTranslations()
    };
}
